import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
} from "@mui/material";
import { IPolicyVehicle } from "../../../models/user/policyDetails/PolicyDetails";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { GetPolicyDetailsHeader, GetPolicyRisks } from "../../../store/user/policyDetails/policyDetailsActions";
import { PostRemoveVehicle } from "../../../store/user/policyChange/policyChangeActions";
import { formatDateToYYYYMMDD } from "../../../utils/Utility";
import AlertDialog from "../../../components/AlertDialog/AlertDialog";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";
import { setCalledReducerType } from "../../../store/common/CommonSlice";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface DeleteVehiclePanelProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
    policyId?: string;
    carrier?: string;
    lobCode?: string;
    system?: string;
    policyNumber?: string;
}

const RESERVED_COLUMNS = new Set(["label", "action", "actions"]);

const DeleteVehiclePanel: React.FC<DeleteVehiclePanelProps> = ({
    card,
    onAction,
    policyId,
    carrier,
    lobCode,
    system,
    policyNumber,
}) => {
    const dispatch = useAppDispatch();
    const sections = card?.sections || [];
    const vehiclesSection =
        sections.find((section: any) => (section?.heading || "").toLowerCase() === "vehicles") ||
        sections[0];
    const vehicles = vehiclesSection?.items || [];
    const { policyVehicles, policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);
    const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
    const { isSuccess, isError, calledReducerType } = useAppSelector((state: RootState) => state.common);
    const [vehicleForDelete, setVehicleForDelete] = useState<IPolicyVehicle | null>(null);
    const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
    const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

    const refs = useMemo(() => card?.entity_refs || {}, [card]);
    const effectivePolicyId = policyId || refs.policy_id || refs.policyId;
    const effectiveCarrier = carrier || refs.carrier;
    const effectiveLob = lobCode || refs.lob_code || refs.lobCode;
    const effectiveSystem = system || refs.system;

    useEffect(() => {
        if (effectivePolicyId && effectiveCarrier && effectiveSystem && effectiveLob) {
            dispatch(
                GetPolicyRisks({
                    policyId: String(effectivePolicyId),
                    carrier: String(effectiveCarrier),
                    system: String(effectiveSystem),
                    lobCode: String(effectiveLob),
                })
            );
        }
    }, [dispatch, effectivePolicyId, effectiveCarrier, effectiveSystem, effectiveLob]);

    useEffect(() => {
        if (effectivePolicyId && effectiveCarrier && effectiveSystem && effectiveLob) {
            dispatch(
                GetPolicyDetailsHeader({
                    policyId: String(effectivePolicyId),
                    carrier: String(effectiveCarrier),
                    system: String(effectiveSystem),
                    lobCode: String(effectiveLob),
                })
            );
        }
    }, [dispatch, effectivePolicyId, effectiveCarrier, effectiveSystem, effectiveLob]);

    useEffect(() => {
        if ((isSuccess || isError) && calledReducerType === "policyChange/PostRemoveVehicle") {
            setMessageDialogOpen(true);
            const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
            if (pn && onAction) {
                onAction({
                    id: "followup",
                    label: "Follow up",
                    followup_message: `Show me details for policy ${pn}`,
                });
            }
        }
    }, [isSuccess, isError, calledReducerType]);

    const columns = useMemo(() => {
        const colSet = new Set<string>();
        vehicles.forEach((vehicle: any) => {
            Object.keys(vehicle || {}).forEach((key) => {
                if (!RESERVED_COLUMNS.has(key)) {
                    colSet.add(key);
                }
            });
        });
        return Array.from(colSet);
    }, [vehicles]);

    const resolveVehicleFromRow = useCallback(
        (row: any, action?: PolicyAction): IPolicyVehicle => {
            const src = { ...(row || {}), ...((action?.params as any) || {}) };
            const vin = src?.vin || src?.VIN || src?.Vin || src?.VehicleVIN || src?.vehicleVin;
            if (vin) {
                const byVin = (policyVehicles || []).find((v: IPolicyVehicle) => (v as any)?.vin === vin);
                if (byVin) return byVin;
            }
            const rowYear = src?.year || src?.Year || src?.manufactureYr || src?.ManufactureYr;
            const rowMake = src?.make || src?.Make;
            const rowModel = src?.model || src?.Model;
            if (rowYear && rowMake && rowModel) {
                const byYmm = (policyVehicles || []).find(
                    (v: IPolicyVehicle) =>
                        String((v as any)?.manufactureYr) === String(rowYear) &&
                        String((v as any)?.make)?.toLowerCase?.() === String(rowMake)?.toLowerCase?.() &&
                        String((v as any)?.model)?.toLowerCase?.() === String(rowModel)?.toLowerCase?.()
                );
                if (byYmm) return byYmm;
            }
            const addr1 =
                (src?.address && (src?.address.addr1 || src?.address.line1)) ||
                src?.AddressLine1 ||
                src?.addressLine1 ||
                src?.addr1 ||
                src?.Addr1 ||
                src?.address ||
                src?.Address ||
                src?.["Garage Address"] ||
                src?.GarageAddress;
            const city = (src?.address && src?.address.city) || src?.city || src?.City;
            const stateProvCd =
                (src?.address && (src?.address.stateProvCd || src?.address.state)) ||
                src?.state ||
                src?.State ||
                src?.stateProvCd ||
                src?.StateProvCd;
            const postalCode =
                (src?.address && (src?.address.postalCode || src?.address.zip)) ||
                src?.zip ||
                src?.Zip ||
                src?.postalCode ||
                src?.PostalCode;
            const make = rowMake;
            const model = rowModel;
            const manufactureYr = rowYear;
            const fallbackVehicle: any = {
                vin,
                make,
                model,
                manufactureYr,
                address: {
                    addr1: addr1,
                    city: city,
                    stateProvCd: stateProvCd,
                    postalCode: postalCode,
                },
            };
            return fallbackVehicle as IPolicyVehicle;
        },
        [policyVehicles]
    );

    const handleDeleteVehicleRequest = useCallback((vehicle: IPolicyVehicle) => {
        setVehicleForDelete(vehicle);
        setAlertDialogOpen(true);
    }, []);

    const handleAlertDialogCancel = useCallback(() => {
        setAlertDialogOpen(false);
        setVehicleForDelete(null);
    }, []);

    const handleAlertDialogOkClick = useCallback(() => {
        setAlertDialogOpen(false);
        console.log( "handleAlertDialogOkClick", vehicleForDelete,
            effectivePolicyId ,
            userDetails ,
            userDetails?.customerId ,
            policyDetailsHeader ,
            policyDetailsHeader?.carrierPolicyNumber ,
            effectiveCarrier ,
            effectiveSystem ,
            effectiveLob ,policyDetailsHeader?.lobCode);
        
        if (
            vehicleForDelete &&
            effectivePolicyId &&
            userDetails &&
            userDetails.customerId &&
            policyDetailsHeader &&
            policyDetailsHeader.carrierPolicyNumber &&
            effectiveCarrier &&
            effectiveSystem &&
            (effectiveLob || policyDetailsHeader.lobCode)
        ) {
            dispatch(
                PostRemoveVehicle({
                    additionalInterestDetails: {
                        loanNumber: "",
                        address: null,
                        interestName: "",
                        interestTypeCd: "",
                        status: "",
                        phoneInfo: "",
                    },
                    carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
                    changeDt: formatDateToYYYYMMDD(new Date()),
                    customerId: userDetails.customerId,
                    detailDescription: "Removing vehicle from the policy",
                    policyRef: String(effectivePolicyId),
                    carrier: String(effectiveCarrier),
                    system: String(effectiveSystem),
                    lobCode: String(effectiveLob || policyDetailsHeader.lobCode),
                    vehicle: {
                        make: vehicleForDelete?.make,
                        model: vehicleForDelete?.model,
                        manufactureYr: vehicleForDelete?.manufactureYr,
                        vehicleType: vehicleForDelete?.vehicleType,
                        vin: vehicleForDelete?.vin,
                        address: null,
                    },
                })
            );
        }
    }, [
        vehicleForDelete,
        effectivePolicyId,
        userDetails,
        policyDetailsHeader,
        effectiveCarrier,
        effectiveSystem,
        effectiveLob,
        dispatch,
    ]);

    const handleMessageDialogClose = useCallback(() => {
        setMessageDialogOpen(false);
        dispatch(setCalledReducerType(null));
        setVehicleForDelete(null);
    }, [dispatch]);

    const handleActionClick = useCallback(
        (action: PolicyAction, rowContext?: any) => {
            if (!action) return;
            const actionId = (action?.id ?? "").toString().toLowerCase();
            const actionLabel = (action?.label ?? "").toString().toLowerCase();
            if (
                rowContext &&
                (actionId === "delete" ||
                    actionLabel === "delete" ||
                    actionLabel.includes("delete") ||
                    actionId === "remove" ||
                    actionLabel === "remove" ||
                    actionLabel.includes("remove"))
            ) {
                const vehicle = resolveVehicleFromRow(rowContext, action);
                handleDeleteVehicleRequest(vehicle);
                return;
            }
            onAction?.(action);
        },
        [handleDeleteVehicleRequest, onAction, resolveVehicleFromRow]
    );

    if (!card || vehicles.length === 0) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #e5e7eb",
                borderRadius: { xs: 1, md: 2 },
                p: { xs: 1, md: 1.5 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
                        {card?.title || "Delete Vehicle"}
                    </Typography>
                    {card?.subtitle && (
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                            {card.subtitle}
                        </Typography>
                    )}
                </Box>

                <TableContainer
                    sx={{
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        "&:hover": {
                            scrollbarWidth: "auto",
                            "&::-webkit-scrollbar": {
                                display: "block",
                                width: "8px",
                                height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "#f1f1f1",
                                borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "#c1c1c1",
                                borderRadius: "4px",
                                "&:hover": {
                                    background: "#a8a8a8",
                                },
                            },
                        },
                    }}
                >
                    <Table size="small" stickyHeader aria-label="delete vehicle table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                        {column}
                                    </TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vehicles.map((vehicle: any, idx: number) => (
                                <TableRow key={`delete_vehicle_${idx}`} sx={{ "&:last-child td": { border: 0 } }}>
                                    {columns.map((column) => (
                                        <TableCell key={`${idx}_${column}`} sx={{ whiteSpace: "nowrap" }}>
                                            <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#374151" }}>
                                                {vehicle?.[column] ?? "-"}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                            {(Array.isArray(vehicle?.actions)
                                                ? vehicle.actions
                                                : vehicle?.actions
                                                ? [vehicle.actions]
                                                : vehicle?.action
                                                ? [vehicle.action]
                                                : []
                                            ).map((action: PolicyAction, actionIdx: number) => (
                                                <Button
                                                    key={`delete_vehicle_action_${idx}_${actionIdx}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleActionClick(action, vehicle)}
                                                    sx={{
                                                        fontSize: { xs: 10, md: 11 },
                                                        py: { xs: 0.5, md: 0.6 },
                                                        px: { xs: 1, md: 1.5 },
                                                        minWidth: "auto",
                                                    }}
                                                >
                                                    {action?.label || "Delete"}
                                                </Button>
                                            ))}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {(card?.cta?.secondary || []).length > 0 && (
                    <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 1, mt: 0.5 }}>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", mb: 0.5 }}>
                            Quick Actions
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 0.75,
                            }}
                        >
                            {(card?.cta?.secondary || []).map((cta: PolicyAction, idx: number) => (
                                <Button
                                    key={`delete_vehicle_secondary_${idx}`}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleActionClick(cta)}
                                    sx={{
                                        fontSize: { xs: 10, md: 12 },
                                        py: { xs: 0.75, md: 0.9 },
                                    }}
                                >
                                    {cta.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}

                {(card?.disclaimers || []).length > 0 && (
                    <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: "#6b7280", mt: 1 }}>
                        {card.disclaimers.join(" ")}
                    </Typography>
                )}
            </Box>
            <AlertDialog
                open={alertDialogOpen}
                handleCancelClick={handleAlertDialogCancel}
                handleOkClick={handleAlertDialogOkClick}
                title={"Removing this vehicle will end its coverage under this policy"}
            />
            <MessageDialog open={messageDialogOpen} handleClose={handleMessageDialogClose} />
        </Paper>
    );
};

export default DeleteVehiclePanel;

